import { IBusiness, IContact } from '@trii/types/dist/Contacts';
import { Spaces } from '@trii/types';
import { CardContent, Box, styled, Popper, ClickAwayListener, SxProps, Theme } from '@mui/material';
import { PhoneEnabled, WhatsApp, Facebook, Instagram, ThreeP, Email } from '@mui/icons-material';
import {
  BusinessSection,
  ContactMethod,
  Header,
  LabelsSection,
  MembersSection,
  Properties,
} from './components';
import { useEffect } from 'react';
import { useTranslation, I18nextProvider } from 'react-i18next';
import i18nInstance from '../../i18n/config';
// Utils
import { isBusiness } from './types/is-business.typeguard';
import { isContact } from './types/is-contact.typeguard';
import formatContactName from '../../utils/formatContactName';

const PopupContainer = styled(Box)(({ theme }) => ({
  //@ts-ignore
  backgroundColor: theme.palette.background.panel,
  boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
  borderRadius: '8px',
  width: 300,
  maxWidth: 300,
  boxSizing: 'border-box',
  maxHeight: 'calc(100vh - 500px)',
  overflowY: 'auto',
  overflowX: 'hidden',
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
  transition: 'opacity 0.3s ease, transform 0.3s ease',
  padding: '8px',
  zIndex: 1300,

  '&::-webkit-scrollbar': {
    width: 5,
  },
  '&::-webkit-scrollbar-track': {
    background: '#E0E0E0',
    borderRadius: 10,
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#9E9E9E',
    borderRadius: 10,
    transition: 'background 0.3s',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#616161',
  },
}));

export interface ContactInfoPopupProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  language?: 'en' | 'es';
  contactData?: IContact | IBusiness;
  avatarImgUrl?: string;
  sx?: SxProps<Theme>;
  navigate: (path: string) => void;
  contactNameFormat?: Spaces.nameFormat;
}

const ContactInfoPopupContent = ({
  open,
  anchorEl,
  onClose,
  contactData,
  avatarImgUrl,
  navigate,
  language = 'en',
  contactNameFormat,
}: ContactInfoPopupProps) => {
  const { t: i18nT, i18n } = useTranslation();

  useEffect(() => {
    if (language) {
      i18n.changeLanguage(language);
    }
  }, [language, i18n]);

  const t = i18nT;
  const dataIsBusiness = isBusiness(contactData);
  const dataIsContact = isContact(contactData);

  const displayName = contactData
    ? dataIsContact
      ? formatContactName(contactData.firstName, contactData.lastName, contactNameFormat)
      : contactData.name
    : undefined;

  const contactMethods = [
    {
      icon: <PhoneEnabled fontSize="small" />,
      title: t('phone'),
      contactList: contactData?.phones || [],
      showTitle: true,
    },
    {
      icon: <Email fontSize="small" />,
      title: t('email'),
      contactList: contactData?.emails || [],
      showTitle: true,
    },
    {
      icon: <WhatsApp fontSize="small" />,
      title: 'IM',
      contactList: contactData?.ims_whatsapp || [],
      showTitle: true,
    },
    {
      icon: <Facebook fontSize="small" />,
      contactList: contactData?.ims_facebook || [],
      showTitle: false,
    },
    {
      icon: <Instagram fontSize="small" />,
      contactList: contactData?.ims_instagram || [],
      showTitle: false,
    },
    {
      icon: <ThreeP fontSize="small" />,
      contactList: contactData?.ims_webchat || [],
      showTitle: false,
    },
  ];

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (open) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose]);

  return (
    <Popper
      sx={{ zIndex: 1300 }}
      open={open}
      anchorEl={anchorEl}
      placement="bottom-start"
      data-popper-child="true"
    >
      <ClickAwayListener onClickAway={onClose}>
        <PopupContainer>
          <CardContent>
            <Header
              navigate={navigate}
              contactId={contactData?.id}
              imgUrl={avatarImgUrl}
              name={displayName}
              onClose={onClose}
              isBusiness={dataIsBusiness}
            />

            {/* Labels */}
            <LabelsSection contactData={contactData} title={t('labels')} />

            {/* Business from the IContact*/}
            {dataIsContact && <BusinessSection contactData={contactData} title={t('business')} />}

            {/* Members from the IBusiness */}
            {dataIsBusiness && (
              <MembersSection
                contactData={contactData}
                title={t('businessMembers')}
                navigate={navigate}
              />
            )}

            {/* Contact Methods */}
            {contactMethods.map((method, index) => (
              <ContactMethod
                key={index}
                icon={method.icon}
                title={method.title}
                contactList={method.contactList}
                showTitle={method.showTitle}
              />
            ))}

            {/* Properties */}
            <Properties properties={contactData?.properties} title={t('properties')} />
          </CardContent>
        </PopupContainer>
      </ClickAwayListener>
    </Popper>
  );
};

const ContactInfoPopup = (props: ContactInfoPopupProps) => {
  return (
    <I18nextProvider i18n={i18nInstance}>
      <ContactInfoPopupContent {...props} />
    </I18nextProvider>
  );
};

export default ContactInfoPopup;
